import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { smoothScrollTo } from "@/lib/lenis";

const HERO_BG =
  "https://images.unsplash.com/photo-1619252584172-a83a949b6efd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG1hY3JvJTIwdGV4dHVyZSUyMHdoaXRlfGVufDB8fHx8MTc4NTkzODgyOXww&ixlib=rb-4.1.0&q=85";

const line1 = "We don't build in";
const line2 = "silos. We fuse.";

const wordReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 + i * 0.12 },
  }),
};

const Line = ({ text, delayBase }) => (
  <span className="block overflow-hidden">
    <motion.span
      custom={delayBase}
      variants={wordReveal}
      initial="hidden"
      animate="show"
      className="block"
    >
      {text}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const shapeRotate = useTransform(scrollYProgress, [0, 1], [0, 45]);

  return (
    <section
      ref={ref}
      data-testid={HERO.section}
      className="relative min-h-[100vh] flex flex-col justify-center px-6 md:px-10 pt-32 pb-20 overflow-hidden"
    >
      <motion.div
        style={{ y }}
        className="absolute inset-0 bg-cover bg-center opacity-[0.14] mix-blend-multiply pointer-events-none"
        aria-hidden="true"
      >
        <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
      </motion.div>

      <div
        className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-brand/20 blur-[110px] pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute top-1/3 -right-10 w-[380px] h-[380px] rounded-full bg-brand-indigo/15 blur-[100px] pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-0 left-1/3 w-[300px] h-[300px] rounded-full bg-brand-teal/15 blur-[100px] pointer-events-none"
        aria-hidden="true"
      />

      <motion.div
        style={{ rotate: shapeRotate }}
        className="hidden md:block absolute -right-24 top-32 w-[420px] h-[420px] border border-brand-indigo/20 rounded-full pointer-events-none"
        aria-hidden="true"
      />
      <motion.div
        style={{ rotate: shapeRotate }}
        className="hidden md:block absolute right-10 top-56 w-[300px] h-[300px] border border-brand/30 pointer-events-none"
        aria-hidden="true"
      />
      <motion.div
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        className="hidden md:block absolute right-40 top-40 w-[220px] h-[220px] pointer-events-none"
        aria-hidden="true"
      >
        <svg viewBox="0 0 100 100" className="w-full h-full opacity-[0.35]">
          <polygon points="50,6 94,90 6,90" fill="none" stroke="#0D9488" strokeWidth="0.6" />
        </svg>
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-5xl">
        <motion.p
          data-testid={HERO.eyebrow}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-xs tracking-[0.25em] uppercase font-medium text-brand mb-6"
        >
          Pulse Agentic Private Limited
        </motion.p>

        <h1
          data-testid={HERO.headline}
          className="font-display text-5xl md:text-6xl lg:text-[5vw] leading-[0.98] tracking-tighter text-ink font-medium"
        >
          <Line text={line1} delayBase={0} />
          <Line text={line2} delayBase={1} />
          <span className="block overflow-hidden">
            <motion.span
              custom={2}
              variants={wordReveal}
              initial="hidden"
              animate="show"
              className="block text-brand"
            >
              Strategy. Tech. Product.
            </motion.span>
          </span>
        </h1>

        <motion.p
          data-testid={HERO.subheadline}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-8 max-w-lg text-lg leading-relaxed text-muted-foreground"
        >
          One unified practice, built by operators who have spent over a decade
          shipping inside enterprise strategy, engineering and product teams —
          now solving for a smaller set of clients, with sharper focus.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.1 }}
          className="mt-10"
        >
          <a
            data-testid={HERO.ctaButton}
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              smoothScrollTo("#contact");
            }}
            className="inline-flex items-center gap-2 border-b border-ink pb-1 text-sm font-medium text-ink hover:text-brand hover:border-brand transition-colors"
          >
            Bring us a problem worth solving
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        data-testid={HERO.scrollIndicator}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-10 left-6 md:left-10 flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted-foreground"
      >
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={14} />
        </motion.span>
        Scroll
      </motion.div>
    </section>
  );
}
